/**
 * FS Mapping — Import review dialog.
 *
 * Extracted from FsMapping.jsx. Pure display + confirm surface — the
 * parent owns the file, summary, and applying state; this dialog only
 * shows the pre-commit review of a validated import and calls back to
 * the parent when the user confirms Apply.
 */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { asArray } from "@/lib/asArray";
import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Check, Loader2 } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { PendingImportSummary } from "@/components/PendingMapping";   // Batch 27 · Add-on 3 · B (M39)

export default function ImportDialog({
  open, onOpenChange, fileName, summary, applying, onCancel, onApply,
}) {
  const colW = useResizableColumns({ storageKey: "fsm-import-dialog", defaults: [110, 240, 160, 240], mins: [72, 130, 100, 130] });   // Step 11d · add-on
  const { lang, dict, t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Review import — {fileName}</DialogTitle>
        </DialogHeader>
        {summary && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2 dark:bg-emerald-950/30 dark:border-emerald-900">
                <div className="text-[10px] uppercase tracking-wider text-emerald-800 dark:text-emerald-200">{t("fsMappingImport.validRows")}</div>
                <div className="text-2xl font-semibold text-emerald-900 dark:text-emerald-100" data-testid="fs-import-valid">
                  {summary.valid_row_count}
                </div>
              </div>
              <div className={`rounded-md border p-2 ${summary.error_count ? "border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900" : "border-slate-300 bg-slate-50 dark:bg-slate-900/40"}`}>
                <div className="text-[10px] uppercase tracking-wider">{t("fsMappingImport.errors")}</div>
                <div className="text-2xl font-semibold" data-testid="fs-import-errors">{summary.error_count}</div>
              </div>
              <div className="rounded-md border border-slate-300 bg-slate-50 p-2 dark:bg-slate-900/40">
                <div className="text-[10px] uppercase tracking-wider">{t("fsMappingImport.matched")}</div>
                <div className="text-2xl font-semibold">{summary.matched_accounts}</div>
              </div>
            </div>
            <PendingImportSummary summary={summary} />
            {summary.error_count > 0 && (
              <div className="rounded-md border border-red-300 bg-red-50 p-2 text-xs dark:border-red-900 dark:bg-red-950/30">
                <div className="mb-1 font-semibold text-red-900 dark:text-red-200">{t("fsMappingImport.skipped")}</div>
                <ul className="max-h-40 space-y-0.5 overflow-auto font-mono text-[11px] text-red-800 dark:text-red-300">
                  {asArray(summary.errors).slice(0, 30).map((e, i) => (
                    <li key={i} data-testid={`fs-import-err-${i}`}>
                      Row {e.row}{e.code ? ` (${e.code})` : ""}: {e.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {summary.preview?.length > 0 && (
              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Preview (first {summary.preview.length} of {summary.valid_row_count})
                </div>
                <div className="max-h-40 overflow-auto rounded-md border text-xs">
                  <ResizableTable cols={colW} className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-900/60">
                      <tr>
                        <ResizableTh index={0} className="px-2 py-1 text-start font-mono text-[10px]">{t("fsMappingImport.code")}</ResizableTh>
                        <ResizableTh index={1} className="px-2 py-1 text-start">{t("fsMappingImport.name")}</ResizableTh>
                        <ResizableTh index={2} className="px-2 py-1 text-start">{t("fsMappingImport.fsBeforeAfter")}</ResizableTh>
                        <ResizableTh index={3} className="px-2 py-1 text-start">{t("fsMappingImport.noteBeforeAfter")}</ResizableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.preview.map((p, i) => (
                        <tr key={i} className={p.changed ? "bg-emerald-50/40 dark:bg-emerald-950/20" : ""}>
                          <td className="px-2 py-1 font-mono text-[10px]">{p.account_code}</td>
                          <td className="px-2 py-1">{p.account_name}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{p.before.fs_mapping || "—"} → <b>{p.after.fs_mapping || "—"}</b></td>
                          <td className="px-2 py-1 font-mono text-[10px]">{p.before.note_mapping || "—"} → <b>{p.after.note_mapping || "—"}</b></td>
                        </tr>
                      ))}
                    </tbody>
                  </ResizableTable>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="fs-import-cancel">{t("common.cancel")}</Button>
          <Button onClick={onApply}
                  disabled={applying || !summary?.valid_row_count}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="fs-import-apply">
            {applying ? <Loader2 className="me-1 h-3 w-3 animate-spin" /> : <Check className="me-1 h-3 w-3" />}
            Apply {fmtPlural(summary?.valid_row_count || 0, lang, dict.plurals.validRows)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
